/**
 * Time — every punch, against the estimate (`docs/visit-page.md` §11, §6.5;
 * Board 7 A, do-not-substitute 9).
 *
 * Per day and person: the bar from 6 AM to 6 PM — **green on this visit**,
 * grey for the yard and the drive, stripes for lunch, the plan dashed behind
 * today's live bar — then the punches in words, *on this visit* in hours (one
 * decimal), *on the clock* in h:mm, and the cost (internal). A click on any
 * punch opens **Edit a punch** (Board 7 B); **Add time** opens Board 18 E's
 * popup in both modes. Every number is the server's (`GET /visits/:id/time`);
 * the page draws minutes onto the bar and prints the rest as given.
 *
 * Desktop draws Board 7's grid (`200px 480px 1fr 92px 84px 118px`). The iPad
 * and the phone are not drawn (lead round 1, item 7): the bar and the punches
 * share the width below a 1290px column, and below 980 (an iPad on its side
 * and every phone) a person is a block — name and numbers, the bar under them
 * at the card's width, the punches under that.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import type {
  DayChip, DayRef, PersonRef, PunchEvent, PunchPlace, TimeDayCard, TimeFlag, TimePersonRow, TimeRead, VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Segmented, StatusChip } from '../components/primitives.js'
import { COMMON, TIME } from './copy.js'
import { dayText, hm, hours1, hoursBare, localTime, money, signed, timeIn } from './format.js'
import { EvBar } from './ev-bar.js'
import { Face } from './face.js'
import { VisitGlyph } from './glyphs.js'
import { useVisitTab, useVisitWrite, type VisitWriteResult } from './api.js'
import { DeltaTag, KpiLabel, LockedTotal, minuteOfDay, useBoxWidth } from './time-money-parts.js'
import { InternalChip } from './internal-chip.js'
import { EditPunchPopup } from './edit-punch.js'
import { AddTimePopup } from './add-time.js'

/** Board 7's person row, and its two narrower readings. */
type Layout = 'full' | 'flex' | 'stacked' | 'phone'

const FULL_FROM = 1290
const FLEX_FROM = 980

/** The grid every row of a day card shares (Board 7) — the cost column only where there is money. */
function gridOf(layout: 'full' | 'flex', hasMoney: boolean): string {
  const lead = layout === 'full' ? '200px 480px minmax(0,1fr)' : '180px minmax(0,1.15fr) minmax(0,1fr)'
  return `${lead} 92px 84px${hasMoney ? ' 118px' : ''}`
}

export function TimeTab({ visitId, read, phone }: { visitId: string; read: VisitRead; phone: boolean }) {
  const { data, refresh } = useVisitTab<TimeRead>(visitId, '/time')
  const write = useVisitWrite(visitId)
  const [params, setParams] = useSearchParams()
  const [show, setShow] = useState<string>('all')
  const [editing, setEditing] = useState<{ card: TimeDayCard; row: TimePersonRow; punch: PunchEvent } | null>(null)
  const [adding, setAdding] = useState(false)
  const [box, width] = useBoxWidth<HTMLDivElement>()

  /* The phone's green **Add time** (lead round 5, item 20): `?tab=time&add=1` opens the popup on arrival. */
  useEffect(() => {
    if (params.get('add') !== '1') return
    setAdding(true)
    const next = new URLSearchParams(params)
    next.delete('add')
    setParams(next, { replace: true })
  }, [params, setParams])

  const layout: Layout = phone ? 'phone' : width === 0 || width >= FULL_FROM ? 'full' : width >= FLEX_FROM ? 'flex' : 'stacked'

  /** A write's answer carries the Time tab re-read (`?tab=time`); the header and the counts go to the page. */
  function written(result: VisitWriteResult) {
    refresh(result.tab as TimeRead | undefined)
  }

  async function lookAt(flag: TimeFlag) {
    written(await write('POST', `/time/flags/${flag.id}/looked-at`, { note: '' }, { tab: 'time' }))
  }

  if (!data) return <div ref={box} className="min-h-[200px]" />

  const hasMoney = data.summary.internal !== undefined
  const canChange = data.add.can
  const cards = show === 'all' ? data.cards : data.cards.filter((card) => String(card.day.index) === show)
  const toCome = show === 'all' ? data.toCome : data.toCome.filter((line) => String(line.day.index) === show)

  return (
    <div ref={box} className="flex min-w-0 flex-col gap-4" {...(phone ? {} : { 'data-as-drawn': 'vp-07 A' })}>
      {/* Toolbar: the days, the legend, **Add time** (the phone's is its green quick action). */}
      <div className="flex items-center gap-3">
        <div className={cn(phone && 'no-scrollbar -mx-4 max-w-[100vw] overflow-x-auto px-4')}>
          <Segmented
            size={phone ? 'touch' : 'toolbar'}
            label={TIME.allDays}
            value={show}
            onChange={setShow}
            options={[
              { value: 'all', label: TIME.allDays },
              ...data.days.map((day) => ({ value: String(day.index), label: TIME.dayChip(String(day.index), dayText(day.date, 'day')) })),
            ]}
          />
        </div>
        {(layout === 'full' || layout === 'flex') && <Legend />}
        {!phone && <span className="flex-1" />}
        {!phone && canChange && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex h-[34px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-button-toolbar border
                       border-line bg-surface px-3.5 text-[12.5px] font-semibold text-text hover:bg-hover"
          >
            <VisitGlyph name="plus" size={14.5} stroke={2} />
            {TIME.addTime}
          </button>
        )}
      </div>

      {/* Narrower than Board 7's grid, the legend takes its own line under the days. */}
      {layout !== 'full' && layout !== 'flex' && <Legend />}

      <Summary time={data} phone={phone} />

      {cards.map((card) => (
        <DayCard
          key={card.day.visitId}
          card={card}
          time={data}
          read={read}
          layout={layout}
          hasMoney={hasMoney}
          canChange={canChange}
          onPunch={(row, punch) => setEditing({ card, row, punch })}
          onLookAt={(flag) => void lookAt(flag)}
        />
      ))}

      {toCome.map((line) => (
        <ToComeLine key={line.day.visitId} day={line.day} planned={line.planned} crew={line.crew} start={line.start} phone={phone} />
      ))}

      {editing && (
        <EditPunchPopup
          read={read}
          card={editing.card}
          row={editing.row}
          punch={editing.punch}
          phone={phone}
          onClose={() => setEditing(null)}
          onSave={async (intervalId, body) => {
            written(await write('PATCH', `/punches/${intervalId}`, body, { tab: 'time' }))
            setEditing(null)
          }}
        />
      )}

      {adding && (
        <AddTimePopup
          visitId={visitId}
          read={read}
          time={data}
          phone={phone}
          onClose={() => setAdding(false)}
          onAdded={(result) => { written(result); setAdding(false) }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The legend and the three cards on top
// ---------------------------------------------------------------------------

/** The lunch stripes — `timeline-line` and `time-lunch-stripe`, 3 px each, at −45° (Board 7). */
const LUNCH_STRIPES = 'repeating-linear-gradient(-45deg, rgb(var(--color-timeline-line)), rgb(var(--color-timeline-line)) 3px, '
  + 'rgb(var(--color-time-lunch-stripe)) 3px, rgb(var(--color-time-lunch-stripe)) 6px)'

function Legend() {
  const item = (swatch: ReactNode, words: string) => (
    <span className="inline-flex items-center gap-[5px] text-[11.5px] text-text-muted">{swatch}{words}</span>
  )
  return (
    <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1">
      {item(<i className="inline-block h-2 w-4 rounded-[3px] bg-accent" />, TIME.legend.onThisVisit)}
      {item(<i className="inline-block h-2 w-4 rounded-[3px] bg-time-away" />, TIME.legend.yardAndDrive)}
      {item(<i className="inline-block h-2 w-4 rounded-[3px]" style={{ background: LUNCH_STRIPES }} />, TIME.legend.lunch)}
      {item(<i className="inline-block h-2 w-4 rounded-[3px] border-[1.5px] border-dashed border-accent-line" />, TIME.legend.planned)}
    </div>
  )
}

/** **ON THIS VISIT** · **LABOR COST** (internal) · **TO LOOK AT** — Board 7, flex 1.3 / 1 / .8. */
function Summary({ time, phone }: { time: TimeRead; phone: boolean }) {
  const { summary } = time
  const card = 'flex min-w-0 flex-col gap-2 rounded-record border border-line px-4 py-[13px]'
  return (
    <div className={cn('flex gap-3', phone && 'flex-col')}>
      <div className={cn(card, 'bg-surface')} style={{ flex: '1.3 1 0' }}>
        <KpiLabel>{TIME.onThisVisit}</KpiLabel>
        <div className="flex flex-wrap items-baseline gap-x-2">
          <b className="text-[22px]">{COMMON.h(hours1(summary.onVisit.hours))}</b>
          <span className="text-[12.5px] text-text-muted">
            {TIME.hoursSub(hours1(summary.onVisit.estimated), hours1(summary.onVisit.projected))}
          </span>
        </div>
        <EvBar bar={summary.onVisit.bar} size="md" />
      </div>
      {summary.internal && (
        <div className={cn(card, 'bg-surface-internal')} style={{ flex: '1 1 0' }}>
          <div className="flex items-center gap-2">
            <KpiLabel>{TIME.laborCost}</KpiLabel>
            <InternalChip />
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2">
            <b className="text-[22px]">{money(summary.internal.soFar)}</b>
            <span className="text-[12.5px] text-text-muted">{TIME.soFar}</span>
          </div>
          <span className="text-[12px] text-text-muted">
            {TIME.laborSub(money(summary.internal.estimated), hoursBare(summary.internal.estimatedHours),
              money(summary.internal.estimateRate), money(summary.internal.projected))}
          </span>
        </div>
      )}
      <div className={cn(card, 'bg-surface')} style={{ flex: '.8 1 0' }}>
        <KpiLabel>{TIME.toLookAt}</KpiLabel>
        <div className="flex items-baseline gap-2">
          <b className="text-[22px]">{summary.flags}</b>
          <span className="text-[12.5px] text-text-muted">{TIME.flags}</span>
        </div>
        <span className="text-[12px] text-text-muted">{TIME.edited(String(summary.edits))}</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// A day
// ---------------------------------------------------------------------------

/** A day's chip — *On the clock · 3* teal, *Closed out* green (Board 7) — in its `visitDay` pair. */
function DayPill({ chip }: { chip: DayChip }) {
  const words = chip.kind === 'on_the_clock' ? TIME.onTheClockCount(String(chip.count))
    : chip.kind === 'prep' ? COMMON.prep(String(chip.ready), String(chip.total))
    : chip.kind === 'closed_out' ? COMMON.closedOut
    : chip.kind === 'on_site' ? COMMON.onSite
    : chip.kind === 'ready' ? COMMON.ready
    : chip.kind === 'scheduled' ? COMMON.scheduled
    : COMMON.skipped
  return <StatusChip group="visitDay" status={chip.kind} className="shrink-0 whitespace-nowrap">{words}</StatusChip>
}

function DayCard({ card, time, read, layout, hasMoney, canChange, onPunch, onLookAt }: {
  card: TimeDayCard
  time: TimeRead
  read: VisitRead
  layout: Layout
  hasMoney: boolean
  canChange: boolean
  onPunch: (row: TimePersonRow, punch: PunchEvent) => void
  onLookAt: (flag: TimeFlag) => void
}) {
  const today = card.day.relative === 'today'
  const relative = card.day.relative === 'today' ? COMMON.today : card.day.relative === 'tomorrow' ? COMMON.tomorrow : null
  const grid = layout === 'full' || layout === 'flex' ? gridOf(layout, hasMoney) : null
  const phone = layout === 'phone'

  return (
    <section className="overflow-hidden rounded-record border border-line bg-surface" data-time-day={card.day.index}>
      <header
        className={cn('flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border-b border-line',
          phone ? 'px-3.5 py-3' : 'px-[18px] py-[13px]', today && 'bg-green-band-bg')}
      >
        <b className="text-[14.5px]">{COMMON.dayN(String(card.day.index))}</b>
        <span className="text-[12.5px] text-text-muted">
          {relative ? TIME.dayDate(dayText(card.day.date, 'date'), relative) : dayText(card.day.date, 'date')}
        </span>
        <DayPill chip={card.chip} />
        <span className="flex-1" />
        <span className="text-[12.5px] text-text-body">
          <b>{COMMON.h(hours1(card.hours.hours))}</b>{' '}
          {card.hours.kind === 'so_far' ? TIME.soFarOfPlanned(hoursBare(card.hours.planned)) : TIME.ofPlanned(hoursBare(card.hours.planned))}
        </span>
        {card.over && (
          <DeltaTag tone={card.over.delta.tone}>
            {card.over.reason
              ? COMMON.dot(COMMON.h(signed(card.over.delta)), card.over.reason)
              : COMMON.h(signed(card.over.delta))}
          </DeltaTag>
        )}
        {card.internal && <LockedTotal>{money(card.internal.cost)}</LockedTotal>}
      </header>

      {grid ? (
        <div className="grid gap-3 border-b border-line bg-surface-alt px-[18px] pb-1 pt-1.5" style={{ gridTemplateColumns: grid }}>
          <div />
          <Axis time={time} card={card} timeZone={read.timeZone} />
        </div>
      ) : (
        <div className={cn('border-b border-line bg-surface-alt pb-1 pt-1.5', phone ? 'px-3.5' : 'px-[18px]')}>
          <Axis time={time} card={card} timeZone={read.timeZone} full />
        </div>
      )}

      {card.people.map((row) => (grid
        ? (
          <GridRow
            key={row.person.id}
            grid={grid}
            row={row}
            card={card}
            time={time}
            read={read}
            canChange={canChange}
            onPunch={onPunch}
            onLookAt={onLookAt}
          />
        )
        : (
          <StackedRow
            key={row.person.id}
            row={row}
            card={card}
            time={time}
            read={read}
            phone={phone}
            canChange={canChange}
            onPunch={onPunch}
            onLookAt={onLookAt}
          />
        )))}
    </section>
  )
}

/** *Day 3 · Fri Oct 23 · tomorrow · 22 h planned · Marco, Sam and Jesse · 7:00 start · nothing punched yet*. */
function ToComeLine({ day, planned, crew, start, phone }: {
  day: DayRef; planned: number; crew: PersonRef[]; start: string | null; phone: boolean
}) {
  const relative = day.relative === 'today' ? COMMON.today : day.relative === 'tomorrow' ? COMMON.tomorrow : null
  const names = crew.map((person) => person.firstName)
  return (
    <section
      className={cn('flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-record border border-line bg-surface',
        phone ? 'px-3.5 py-3' : 'px-[18px] py-[13px]')}
    >
      <b className="text-[14.5px]">{COMMON.dayN(String(day.index))}</b>
      <span className="text-[12.5px] text-text-muted">
        {relative ? TIME.dayDate(dayText(day.date, 'date'), relative) : dayText(day.date, 'date')}
      </span>
      <span className="flex-1" />
      <span className="text-[12.5px] text-text-muted">
        {start
          ? TIME.notStarted(hoursBare(planned), names, localTime(start))
          : COMMON.dot(COMMON.hoursPlanned(hoursBare(planned)), COMMON.list(names))}
      </span>
    </section>
  )
}

// ---------------------------------------------------------------------------
// The bar and its axis
// ---------------------------------------------------------------------------

/** A minute's place along the 6 AM – 6 PM axis, in percent of the bar (the server's minutes, drawn). */
function at(time: TimeRead, minute: number): number {
  const { from, to } = time.axis
  return ((minute - from) / (to - from)) * 100
}

/** The hours the axis labels, two apart — 6 AM … 6 PM. */
function axisMinutes(time: TimeRead): number[] {
  const out: number[] = []
  for (let minute = time.axis.from; minute <= time.axis.to; minute += 120) out.push(minute)
  return out
}

function hourLabel(minute: number): string {
  const hour = Math.floor(minute / 60)
  return `${hour % 12 || 12} ${hour < 12 ? 'AM' : 'PM'}`
}

/** The axis row — *6 AM … 6 PM*, and today's red *9:41*. In Board 7's grid it is 470 inside a 480 column. */
function Axis({ time, card, timeZone, full = false }: {
  time: TimeRead; card: TimeDayCard; timeZone: string; full?: boolean
}) {
  return (
    <div className="relative h-3.5" style={{ width: full ? '100%' : 'calc(100% - 10px)' }}>
      {axisMinutes(time).map((minute) => (
        <span
          key={minute}
          className="absolute -translate-x-1/2 whitespace-nowrap text-[10px] font-semibold text-text-faint"
          style={{ left: `${at(time, minute)}%` }}
        >
          {hourLabel(minute)}
        </span>
      ))}
      {card.live && (
        <span
          className="absolute top-[-1px] inline-flex h-4 -translate-x-1/2 items-center rounded-[4px] bg-danger px-[5px]
                     text-[9.5px] font-bold text-white"
          style={{ left: `${at(time, card.live.minute)}%` }}
          data-time-now=""
        >
          {timeIn(timeZone, card.live.at, 'clock')}
        </span>
      )}
    </div>
  )
}

/**
 * One person's day on the bar: the yard and the drive grey, the visit green,
 * lunch striped; today's plan dashed behind it and a ring where the live bar
 * ends; a 2 px amber line and a pencil where a punch was edited.
 */
function PersonBar({ row, card, time, read, full = false }: {
  row: TimePersonRow; card: TimeDayCard; time: TimeRead; read: VisitRead; full?: boolean
}) {
  const edited = row.punches.filter((punch) => punch.edited)
  return (
    <div className="relative h-6" style={{ width: full ? '100%' : 'calc(100% - 10px)' }} data-time-bar="">
      {axisMinutes(time).map((minute) => (
        <i key={minute} className="absolute inset-y-0 block w-px bg-line-faint" style={{ left: `${at(time, minute)}%` }} />
      ))}
      {card.plan && (
        <i
          className="absolute top-[5px] block h-3.5 rounded-[4px] border-[1.5px] border-dashed border-accent-line"
          style={{ left: `${at(time, card.plan.from)}%`, width: `${at(time, card.plan.to) - at(time, card.plan.from)}%` }}
        />
      )}
      {row.bar.map((segment) => (
        <i
          key={`${segment.kind}-${segment.from}`}
          className={cn('absolute top-1.5 block h-3 rounded-[4px]',
            segment.kind === 'visit' ? 'bg-accent' : segment.kind === 'away' ? 'bg-time-away' : '')}
          style={{
            left: `${at(time, segment.from)}%`,
            width: `${at(time, segment.to) - at(time, segment.from)}%`,
            ...(segment.kind === 'lunch' ? { background: LUNCH_STRIPES } : {}),
          }}
          data-segment={segment.kind}
        />
      ))}
      {row.bar.filter((segment) => segment.live).map((segment) => (
        <i
          key={`live-${segment.to}`}
          className="absolute top-2 block h-2 w-2 -translate-x-1/2 rounded-full border-2 border-accent bg-surface"
          style={{ left: `${at(time, segment.to)}%` }}
        />
      ))}
      {edited.map((punch) => {
        const left = at(time, minuteOfDay(read.timeZone, punch.at))
        return (
          <span key={`edit-${punch.kind}`} data-time-edited={punch.kind}>
            <i className="absolute top-0.5 block h-5 w-0.5 -translate-x-1/2 rounded-sm bg-warn" style={{ left: `${left}%` }} />
            <span
              className="absolute top-[-12px] inline-flex h-3.5 w-[18px] -translate-x-1/2 items-center justify-center rounded-[4px]
                         bg-warn-bg text-warn"
              style={{ left: `${left}%` }}
            >
              <VisitGlyph name="pencil" size={10} stroke={2.4} />
            </span>
          </span>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The punches in words
// ---------------------------------------------------------------------------

/** *in · yard* · *in · Keystone* · *in · Keystone, returned the excavator* · *out · yard*. */
function placeWord(place: PunchPlace | null, read: VisitRead): string {
  if (place?.kind === 'yard') return TIME.punch.yard
  if (place?.kind === 'vendor') return place.vendor.shortName
  return read.header.meta.property?.line1 ?? ''
}

interface Said {
  key: string
  /** The times, in bold, each one a press that opens Edit a punch. */
  times: PunchEvent[]
  words: string
  edited: boolean
}

/** A person's punches as the board writes them: *6:30 in · yard  7:00 arrived  12:00–12:30 lunch  5:00 left  5:25 out · yard*. */
function sayPunches(row: TimePersonRow, read: VisitRead): Said[] {
  const said: Said[] = []
  const lunchStart = row.punches.find((punch) => punch.kind === 'lunch_start') ?? null
  const lunchEnd = row.punches.find((punch) => punch.kind === 'lunch_end') ?? null
  for (const punch of row.punches) {
    switch (punch.kind) {
      case 'clock_in': {
        const place = punch.place
        const words = place?.kind === 'vendor' && place.errand?.kind === 'rental_return'
          ? TIME.punch.inReturned(place.vendor.shortName, place.errand.noun)
          : TIME.punch.in(placeWord(place, read))
        said.push({ key: punch.kind, times: [punch], words, edited: Boolean(punch.edited) })
        break
      }
      case 'arrived':
        said.push({ key: punch.kind, times: [punch], words: TIME.punch.arrived, edited: Boolean(punch.edited) })
        break
      case 'lunch_start': {
        const times = lunchEnd ? [punch, lunchEnd] : [punch]
        const endAdded = lunchEnd?.edited?.action === 'added' ? lunchEnd.edited.by : null
        said.push({
          key: 'lunch',
          times,
          words: endAdded ? TIME.punch.lunchEndAddedBy(endAdded.firstName) : TIME.punch.lunch,
          edited: Boolean(punch.edited || lunchEnd?.edited),
        })
        break
      }
      case 'lunch_end':
        if (!lunchStart) said.push({ key: punch.kind, times: [punch], words: TIME.punch.lunch, edited: Boolean(punch.edited) })
        break
      case 'left':
        said.push({ key: punch.kind, times: [punch], words: TIME.punch.left, edited: Boolean(punch.edited) })
        break
      case 'clock_out':
        said.push({ key: punch.kind, times: [punch], words: TIME.punch.out(placeWord(punch.place, read)), edited: Boolean(punch.edited) })
        break
    }
  }
  return said
}

function PunchWords({ row, read, canChange, onPunch, phone = false }: {
  row: TimePersonRow; read: VisitRead; canChange: boolean; onPunch: (punch: PunchEvent) => void; phone?: boolean
}) {
  const said = sayPunches(row, read)
  const arrivedAt = said.findIndex((item) => item.key === 'arrived')
  return (
    <div className={cn('flex flex-wrap gap-x-2.5 gap-y-1 leading-[1.35] text-text-body', phone ? 'text-[12.5px]' : 'text-[11.5px]')}>
      {said.map((item, index) => (
        <span key={item.key} className="contents">
          <span className={cn('whitespace-nowrap', item.edited && 'font-semibold text-warn')} data-punch={item.key}>
            <b className="font-bold text-text">
              {item.times.map((punch, at) => (
                <span key={punch.kind}>
                  {at > 0 && '–'}
                  {canChange ? (
                    <button
                      type="button"
                      onClick={() => onPunch(punch)}
                      className={cn('font-bold hover:underline hover:decoration-dashed hover:underline-offset-2',
                        /* A thumb's 44px around a time on a phone, drawn nowhere (§19.2). */
                        phone && "relative before:absolute before:-inset-x-1 before:-inset-y-3.5 before:content-['']")}
                      data-punch-time={punch.kind}
                    >
                      {timeIn(read.timeZone, punch.at, 'clock')}
                    </button>
                  ) : timeIn(read.timeZone, punch.at, 'clock')}
                </span>
              ))}
            </b>{' '}
            {item.words}
          </span>
          {row.onSiteNow && index === arrivedAt && (
            <span className="whitespace-nowrap font-semibold text-teal-band-text">{TIME.punch.onSiteNow}</span>
          )}
        </span>
      ))}
    </div>
  )
}

/** A flag, amber on its row, with **Look at it** (§11). */
function FlagLine({ flag, canChange, onLookAt }: { flag: TimeFlag; canChange: boolean; onLookAt: (flag: TimeFlag) => void }) {
  const words = flag.kind === 'open'
    ? TIME.flag.open
    : flag.kind === 'far'
      ? (flag.edge === 'out' ? TIME.flag.clockedOut : TIME.flag.clockedIn)(TIME.flag.distance(String(flag.miles ?? '')))
      : TIME.flag.overlaps(flag.other?.reference ?? '')
  return (
    <span className="inline-flex items-center gap-2 text-[11.5px] font-semibold text-warn" data-time-flag={flag.kind}>
      <VisitGlyph name="warn" size={12} stroke={2.4} />
      {words}
      {canChange && !flag.lookedAt && (
        <button
          type="button"
          onClick={() => onLookAt(flag)}
          className="inline-flex h-6 items-center rounded-button border border-line bg-surface px-2 text-[11.5px] font-semibold
                     text-text hover:bg-hover"
        >
          {TIME.lookAtIt}
        </button>
      )}
    </span>
  )
}

// ---------------------------------------------------------------------------
// A person's row
// ---------------------------------------------------------------------------

function Who({ row, size = 30 }: { row: TimePersonRow; size?: number }) {
  return (
    <div className="flex min-w-0 items-center gap-[9px]">
      <Face person={row.person} size={size} />
      <div className="flex min-w-0 flex-col">
        <b className="truncate text-[13px]">{row.person.name}</b>
        <span className="text-[11.5px] text-text-muted">{TIME.role[row.role]}</span>
      </div>
    </div>
  )
}

function OnVisit({ row }: { row: TimePersonRow }) {
  return (
    <div className="flex flex-col items-end">
      <b className="whitespace-nowrap text-[14px]">{COMMON.h(hours1(row.onVisit.hours))}</b>
      <span className="whitespace-nowrap text-[11px] text-text-muted">{TIME.onThisVisitHours}</span>
    </div>
  )
}

function OnClock({ row }: { row: TimePersonRow }) {
  return (
    <div className="flex flex-col items-end">
      <span className="whitespace-nowrap text-[12.5px] font-semibold">{hm(row.onClock.minutes)}</span>
      <span className="whitespace-nowrap text-[11px] text-text-muted">{TIME.onTheClock}</span>
    </div>
  )
}

function Cost({ row, className }: { row: TimePersonRow; className?: string }) {
  if (!row.internal) return null
  return (
    <div className={cn('flex flex-col items-end justify-center bg-surface-internal', className)}>
      <span className="whitespace-nowrap text-[13px]">{money(row.internal.cost)}</span>
      <span className="whitespace-nowrap text-[11px] text-text-faint">{COMMON.per(money(row.internal.rate), 'h')}</span>
    </div>
  )
}

function GridRow({ grid, row, card, time, read, canChange, onPunch, onLookAt }: {
  grid: string
  row: TimePersonRow
  card: TimeDayCard
  time: TimeRead
  read: VisitRead
  canChange: boolean
  onPunch: (row: TimePersonRow, punch: PunchEvent) => void
  onLookAt: (flag: TimeFlag) => void
}) {
  return (
    <div
      className="grid min-h-16 items-stretch gap-3 border-b border-line-faint px-[18px] py-2 text-[13px]"
      style={{ gridTemplateColumns: grid }}
      data-time-person={row.person.id}
    >
      <div className="flex min-w-0 items-center"><Who row={row} /></div>
      <div className="flex min-w-0 items-center"><PersonBar row={row} card={card} time={time} read={read} /></div>
      <div className="flex min-w-0 flex-col justify-center gap-1">
        <PunchWords row={row} read={read} canChange={canChange} onPunch={(punch) => onPunch(row, punch)} />
        {row.flags.map((flag) => <FlagLine key={flag.id} flag={flag} canChange={canChange} onLookAt={onLookAt} />)}
      </div>
      <div className="flex min-w-0 items-center justify-end"><OnVisit row={row} /></div>
      <div className="flex min-w-0 items-center justify-end"><OnClock row={row} /></div>
      {row.internal && <Cost row={row} className="-my-2 border-l border-line-soft px-2.5 py-2" />}
    </div>
  )
}

/** iPad on its side and a phone (not drawn — lead round 1, item 7): a person is a block. */
function StackedRow({ row, card, time, read, phone, canChange, onPunch, onLookAt }: {
  row: TimePersonRow
  card: TimeDayCard
  time: TimeRead
  read: VisitRead
  phone: boolean
  canChange: boolean
  onPunch: (row: TimePersonRow, punch: PunchEvent) => void
  onLookAt: (flag: TimeFlag) => void
}) {
  return (
    <div
      className={cn('flex flex-col gap-2.5 border-b border-line-faint last:border-b-0', phone ? 'px-3.5 py-3' : 'px-[18px] py-3')}
      data-time-person={row.person.id}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1"><Who row={row} /></div>
        <OnVisit row={row} />
        {!phone && <OnClock row={row} />}
        {!phone && <Cost row={row} className="rounded-[8px] border border-line-soft px-2.5 py-1.5" />}
      </div>
      <PersonBar row={row} card={card} time={time} read={read} full />
      <PunchWords row={row} read={read} canChange={canChange} onPunch={(punch) => onPunch(row, punch)} phone={phone} />
      {row.flags.map((flag) => <FlagLine key={flag.id} flag={flag} canChange={canChange} onLookAt={onLookAt} />)}
      {phone && (
        <div className="flex items-center gap-3">
          <span className="text-[12.5px]">
            <b className="font-semibold">{hm(row.onClock.minutes)}</b>{' '}
            <span className="text-text-muted">{TIME.onTheClock}</span>
          </span>
          <span className="flex-1" />
          {row.internal && (
            <span className="inline-flex items-center gap-1.5 rounded-[8px] border border-line-soft bg-surface-internal px-2.5 py-1.5 text-[12.5px]">
              {money(row.internal.cost)}
              <span className="text-[11px] text-text-faint">{COMMON.per(money(row.internal.rate), 'h')}</span>
            </span>
          )}
        </div>
      )}
    </div>
  )
}
